import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// third-party
// import { IntlProvider, MessageFormatElement } from 'react-intl';
import { IntlProvider, MessageFormatElement } from 'react-intl';

// project-imports
import useConfig from 'hooks/useConfig';
import { I18n } from 'types/config';
import { getAvailableUiLanguages } from 'utils/countryMarket';

// load locales files
const loadLocaleData = (locale: I18n) => {
  switch (locale) {
    case 'es':
      return import('utils/locales/es.json');
    case 'pt':
      return import('utils/locales/pt.json');
    case 'ka':
      return import('utils/locales/ka.json');
    case 'ru':
      return import('utils/locales/ru.json');
    // case 'en':
    default:
      return import('utils/locales/en.json');
  }
};

export const allowedLanguages = [
  {
    key: 'de',
    value: 'Alemán',
    original: 'Deutsch'
  },
  {
    key: 'en',
    value: 'Inglés',
    original: 'English'
  },
  {
    key: 'es',
    value: 'Español',
    original: 'Español'
  },
  {
    key: 'fr',
    value: 'Francés',
    original: 'Français'
  },
  {
    key: 'nl',
    value: 'Holandés',
    original: 'Nederlands'
  },
  {
    key: 'pl',
    value: 'Polaco',
    original: 'Polski'
  },
  {
    key: 'pt',
    value: 'Portugués',
    original: 'Português'
  },
  {
    key: 'ka',
    value: 'Georgiano',
    original: 'ქართული'
  },
  {
    key: 'ru',
    value: 'Ruso',
    original: 'Русский'
  }
];

type MessageType = Record<string, string | MessageFormatElement[]>;
const localeMessagesCache = new Map<I18n, MessageType>();

export const USER_LANGUAGE = '__usrlng__';
export const USER_CONTEXT_CODE = '__usrctxcode__';
export const USER_COUNTRY_CODE = '__usrcountry__';

function flattenMessages(nestedMessages: any, prefix = ''): MessageType {
  return Object.keys(nestedMessages).reduce((messages, key) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string' || Array.isArray(value)) {
      messages[prefixedKey] = value;
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }

    return messages;
  }, {} as MessageType);
}

const getBrowserLanguage = () => {
  const browserLanguage: any = navigator.language.split('-')[0];
  const validLanguages = allowedLanguages.map((lang: any) => lang.key);
  return validLanguages.includes(browserLanguage) ? browserLanguage : 'en';
};

// ==============================|| LOCALIZATION ||============================== //

interface Props {
  children: ReactNode;
}

const Locales = ({ children }: Props) => {
  const { i18n, onChangeLocalization } = useConfig();
  const [messages, setMessages] = useState<MessageType | undefined>(undefined);
  const location = useLocation();

  const signupContextCode = (() => {
    const params = new URLSearchParams(location.search);
    return params.get('code');
  })();
  const storedUserCountry = localStorage.getItem(USER_COUNTRY_CODE);
  const contextCode = signupContextCode || localStorage.getItem(USER_CONTEXT_CODE);

  const availableUiLanguages = getAvailableUiLanguages({
    contextCode,
    userCountry: storedUserCountry
  });

  useEffect(() => {
    const storedLanguage = localStorage.getItem(USER_LANGUAGE) as I18n | null;
    const defaultContextLanguage = availableUiLanguages.includes('pt') ? ('pt' as I18n) : getBrowserLanguage();
    const initialLanguage = storedLanguage || defaultContextLanguage;
    const safeInitialLanguage = availableUiLanguages.includes(initialLanguage) ? initialLanguage : ('es' as I18n);

    if (!storedLanguage) {
      localStorage.setItem(USER_LANGUAGE, safeInitialLanguage);
      if (safeInitialLanguage !== i18n) {
        onChangeLocalization(safeInitialLanguage);
        return;
      }
    } else if (!availableUiLanguages.includes(storedLanguage)) {
      localStorage.setItem(USER_LANGUAGE, 'es');
      if (i18n !== 'es') {
        onChangeLocalization('es');
        return;
      }
    } else if (storedLanguage !== i18n) {
      onChangeLocalization(storedLanguage);
      return;
    }

    const cachedMessages = localeMessagesCache.get(i18n);
    if (cachedMessages) {
      setMessages(cachedMessages);
      return;
    }

    loadLocaleData(i18n)
      .then((d: { default: any }) => {
        const flatMessages: MessageType = flattenMessages(d.default);
        localeMessagesCache.set(i18n, flatMessages);
        setMessages(flatMessages as any);
      })
      .catch((error) => {
        console.error('Error loading locale data:', error);
      });
    // onChangeLocalization is intentionally omitted to avoid re-running this effect
    // on every provider render due function identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableUiLanguages, i18n]);

  return (
    <>
      {messages && (
        <IntlProvider locale={i18n} defaultLocale={i18n} messages={messages as Record<string, string>}>
          {children}
        </IntlProvider>
      )}
    </>
  );
};

// const Locales = ({ children }: Props) => {
//   const { i18n } = useConfig();

//   const [messages, setMessages] = useState<any>();

//   useEffect(() => {
//     loadLocaleData(i18n).then((d: { default: any }) => {
//       setMessages(flattenMessages(d.default));
//     });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [i18n]);

//   const flattenMessages = (nestedMessages: { [x: string]: any } | null, prefix = '') => {
//     if (nestedMessages === null) return {};
//     return Object.keys(nestedMessages).reduce((messages: any, key) => {
//       const value = nestedMessages[key];
//       const prefixedKey = prefix ? `${prefix}.${key}` : key;

//       if (typeof value === 'object') {
//         Object.assign(messages, flattenMessages(value, prefixedKey));
//       } else {
//         messages[prefixedKey] = value;
//       }

//       return messages;
//     }, {});
//   };

//   return (
//     <>
//       {messages && (
//         <IntlProvider locale={i18n} defaultLocale="en" messages={messages}>
//           {children}
//         </IntlProvider>
//       )}
//     </>
//   );
// };

export default Locales;
